import React from "react";
import { Helmet } from "react-helmet";
import { motion } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";
import { handleCheckout } from "../../utils/checkout";
import { useNavigate } from "react-router-dom";
import { analytics, EVENT_FN } from "../../analytics";
import { DIGITAL_PRICES } from "../../constant";

interface Product {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  benefits: string[];
  price: number;
  image: string;
  tag?: string;
}

const PRODUCTS: Product[] = [
  {
    id: "resume-kit",
    title: "영국 이력서 템플릿",
    subtitle: "UK CV Template",
    description:
      "영국 채용 시장에 최적화된 ATS 친화적 CV 템플릿. 영국 고용주가 기대하는 형식과 톤으로 구성되어 있습니다.",
    benefits: [
      "ATS 통과율을 높이는 영국 표준 포맷",
      "영국식 예시 문구 포함",
      "PDF / 워드 편집 가능 파일 제공",
    ],
    price: DIGITAL_PRICES["resume-kit"],
    image: "/images/digital_product/preview-resume-kit.png",
    tag: "베스트셀러",
  },
  {
    id: "linkedin-guide",
    title: "LinkedIn 최적화 가이드",
    subtitle: "LinkedIn Profile Guide for the UK Market",
    description:
      "영국 리크루터에게 발견될 수 있는 LinkedIn 프로필 구축 전략. 단순 번역이 아닌 영국 시장용 포지셔닝.",
    benefits: [
      "LinkedIn 의 진짜 역할과 영국 채용 시장에서의 중요성 이해",
      "Headline·About 섹션 고성과 예시 템플릿",
      "커넥션 확장 및 채용 담당자 DM 스크립트",
    ],
    price: DIGITAL_PRICES["linkedin-guide"],
    image: "/images/digital_product/linkedin.png",
  },
  {
    id: "interview-guide-student",
    title: "인터뷰 가이드 — 학생 / 워홀",
    subtitle: "Interview Guide for Students & WHV",
    description:
      "영국 취업을 처음 준비하는 분들을 위한 실전 인터뷰 대비 가이드. 영국식 STAR 기법과 자주 나오는 질문 유형을 중심으로 구성되어 있습니다.",
    benefits: [
      "30+ 실전 Q&A (영국식 STAR 기법 답변 예시)",
      "마지막 질문으로 인상 남기는 법",
      "면접 후 팔로업 이메일 템플렛",
    ],
    price: DIGITAL_PRICES["interview-guide-student"],
    image: "/images/digital_product/eng_interview_playbook.png",
  },
  {
    id: "interview-guide-experienced",
    title: "인터뷰 가이드 — 경력직",
    subtitle: "Interview Guide for Experienced Professionals",
    description:
      "한국 경력을 영국 취업에 연결하려는 분들을 위한 고급 인터뷰 전략. 역량 기반 면접(CBI) 완벽 대비.",
    benefits: [
      "역량 기반 면접(Competency-Based) 완벽 대비",
      "한국 경력을 영국 맥락으로 재프레이밍하는 법",
      "전략적 질문으로 면접관을 설득하는 법",
    ],
    price: DIGITAL_PRICES["interview-guide-experienced"],
    image: "/images/digital_product/kor_interview_playbook.png",
  },
  
];

const BUNDLE_PRICE = DIGITAL_PRICES["digital-bundle"];
const BUNDLE_ORIGINAL = PRODUCTS.reduce((s, p) => s + p.price, 0);

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const ResourcesPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="font-sans bg-white text-[#0A1A3E]">
      <Helmet>
        <title>영국 취업 준비 자료 — BeMe Consulting</title>
        <meta
          name="description"
          content="영국 취업을 위한 실전 자료 모음. 영국 이력서 템플릿, 인터뷰 가이드, LinkedIn 최적화 가이드로 영국 커리어를 시작하세요. BeMe Consulting 제공."
        />
        <meta
          name="keywords"
          content="영국 취업, 영국 취업 준비, 영국 커리어, 영국 이력서, UK CV, 영국 인터뷰, LinkedIn 영국"
        />
        <link rel="canonical" href="https://www.bemeconsulting.com/#/resources" />
        <meta property="og:title" content="영국 취업 준비 자료 — BeMe Consulting" />
        <meta
          property="og:description"
          content="영국 이력서 템플릿, 인터뷰 가이드, LinkedIn 최적화 가이드. 영국 취업 준비의 시작을 BeMe와 함께."
        />
      </Helmet>

      {/* ── Hero ── */}
      <section className="bg-[#0A1A3E] text-white py-20 px-6 text-center">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          className="max-w-2xl mx-auto"
        >
          <span className="inline-block text-xs font-semibold tracking-widest text-gold uppercase mb-4">
            Digital Resources
          </span>
          <h1 className="text-3xl md:text-5xl font-extrabold leading-tight mb-4">
            영국 취업 준비,<br />
            <span className="text-gold">제대로 된 자료</span>로 시작하세요
          </h1>
          <p className="text-white/70 text-base md:text-lg leading-relaxed mb-8">
            영국 커리어 전문가가 직접 만든 실전 가이드와 템플릿.
            <br className="hidden md:block" />
            시행착오 없이 영국 채용 시장의 기준에 맞춰 준비하세요.
          </p>
        </motion.div>
      </section>

      {/* ── Trust bar ── */}
      <div className="bg-[#F5F6F7] border-b border-gray-100 py-4 px-6">
        <div className="max-w-4xl mx-auto flex flex-wrap justify-center gap-x-8 gap-y-2 text-sm text-gray-500 text-center">
          {[
            "즉시 다운로드 가능",
            "영국 취업 전문가 제작",
            "구매 후 이메일로 전달",
            "실제 합격자 피드백 반영",
          ].map((t) => (
            <span key={t} className="flex items-center gap-1.5">
              <Check size={13} className="text-[#0A1A3E]" />
              {t}
            </span>
          ))}
        </div>
      </div>

      {/* ── Products ── */}
      <section id="products" className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-extrabold text-center mb-3">
            영국 취업 준비 자료
          </h2>
          <p className="text-center text-gray-500 text-sm mb-12">
            영국 커리어에 필요한 핵심 자료를 단계별로 준비했습니다
          </p>

          <div className="grid md:grid-cols-2 gap-8">
            {PRODUCTS.map((product, i) => (
              <motion.article
                key={product.id}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.15 }}
                variants={{ ...fadeUp, visible: { ...fadeUp.visible, transition: { duration: 0.5, delay: i * 0.08 } } }}
                className="bg-white border border-gray-100 rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow flex flex-col"
              >
                {/* Preview image */}
                <div className="relative bg-[#F5F6F7] aspect-[16/9] overflow-hidden">
                  <img
                    src={product.image}
                    alt={`${product.title} 미리보기`}
                    className="w-full h-full object-cover object-top"
                    loading="lazy"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = "none";
                    }}
                  />
                  {product.tag && (
                    <span className="absolute top-3 left-3 bg-[#0A1A3E] text-gold text-[10px] font-bold px-2.5 py-1 rounded-full">
                      {product.tag}
                    </span>
                  )}
                </div>

                {/* Card body */}
                <div className="p-6 flex flex-col flex-1">
                  <p className="text-xs font-semibold tracking-widest text-[#d4a017] uppercase mb-1">
                    {product.subtitle}
                  </p>
                  <h3 className="text-lg font-extrabold text-[#0A1A3E] mb-2">
                    {product.title}
                  </h3>
                  <p className="text-gray-500 text-sm leading-relaxed mb-4">
                    {product.description}
                  </p>

                  <ul className="space-y-2 mb-6">
                    {product.benefits.map((b) => (
                      <li key={b} className="flex items-start gap-2 text-sm text-gray-700">
                        <Check size={14} className="text-[#0A1A3E] mt-0.5 shrink-0" />
                        {b}
                      </li>
                    ))}
                  </ul>

                  <div className="mt-auto flex items-center justify-between pt-4 border-t border-gray-100">
                    <div>
                      <span className="text-2xl font-extrabold text-[#0A1A3E]">
                        £{product.price}
                      </span>
                      <span className="text-xs text-gray-400 ml-1">일회 결제</span>
                    </div>
                    <button
                      onClick={() => {
                        analytics.track(EVENT_FN.paymentLink(product.id, "resourcesPage"));
                        handleCheckout(product.id);
                      }}
                      className="bg-[#0A1A3E] text-white text-sm font-semibold px-5 py-2.5 rounded-full hover:bg-[#305485] transition-colors"
                    >
                      바로 구매하기
                    </button>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* ── Bundle ── */}
      <section className="bg-[#0A1A3E] py-20 px-6">
        <div className="max-w-3xl mx-auto text-center text-white">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeUp}
          >
            <span className="inline-block text-xs font-bold tracking-widest text-gold uppercase mb-3">
              Best Value
            </span>
            <h2 className="text-2xl md:text-4xl font-extrabold mb-3">
              All-in-One 영국 취업 키트
            </h2>
            <p className="text-white/70 text-sm md:text-base leading-relaxed mb-8">
              이력서 템플릿 + 인터뷰 가이드 2종 + LinkedIn 가이드.<br />
              영국 취업 준비에 필요한 모든 자료를 한 번에.
            </p>

            <div className="flex justify-center items-baseline gap-3 mb-8">
              <span className="text-white/40 line-through text-xl">£{BUNDLE_ORIGINAL}</span>
              <span className="text-gold text-5xl font-extrabold">£{BUNDLE_PRICE}</span>
              <span className="bg-gold text-[#0A1A3E] text-xs font-bold px-2 py-1 rounded-full">
                {Math.round((1 - BUNDLE_PRICE / BUNDLE_ORIGINAL) * 100)}% 할인
              </span>
            </div>

            <ul className="grid grid-cols-2 gap-3 max-w-md mx-auto mb-10 text-left">
              {[
                "영국 이력서 템플릿",
                "인터뷰 가이드 (학생/워홀)",
                "인터뷰 가이드 (경력직)",
                "LinkedIn 최적화 가이드",
              ].map((item) => (
                <li key={item} className="flex items-center gap-2 text-sm text-white/80">
                  <Check size={13} className="text-gold shrink-0" />
                  {item}
                </li>
              ))}
            </ul>

            <button
              onClick={() => {
                analytics.track(EVENT_FN.paymentLink("digital-bundle", "resourcesPage"));
                handleCheckout("digital-bundle");
              }}
              className="bg-gold text-[#0A1A3E] font-extrabold text-base px-10 py-4 rounded-full hover:bg-yellow-300 transition-colors"
            >
              All-in-One 키트 구매하기 — £{BUNDLE_PRICE}
            </button>
            <p className="text-white/40 text-xs mt-3">구매 후 이메일로 즉시 전달됩니다</p>
          </motion.div>
        </div>
      </section>

      {/* ── CTA to main consulting ── */}
      <section className="py-20 px-6 bg-[#F5F6F7]">
        <div className="max-w-2xl mx-auto text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeUp}
          >
            <h2 className="text-2xl md:text-3xl font-extrabold text-[#0A1A3E] mb-4">
              자료만으로는 부족하다면?
            </h2>
            <p className="text-gray-500 text-sm md:text-base leading-relaxed mb-8">
              영국 커리어 전문가와 1:1로 CV 피드백, 인터뷰 코칭, 취업 전략을 함께 세워보세요.
              <br />
              단순한 교정이 아니라, 당신의 이야기를 영국 채용 시장에 맞게 재구성합니다.
            </p>
            <button
              onClick={() => navigate("/service")}
              className="inline-flex items-center gap-2 bg-[#0A1A3E] text-white font-bold px-7 py-3.5 rounded-full text-sm hover:bg-[#305485] transition-colors"
            >
              1:1 컨설팅 서비스 알아보기 <ArrowRight size={15} />
            </button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ResourcesPage;
